// Analytics.js
// Dashboard de visitas. #added 20/9/26 TG
// Entra en /admin/analytics con la passphrase de analytics-config.php.
// Mismo patrón de login que BankApprovals.js.

import React, { useState, useContext, useCallback, useEffect } from "react";
import { AppContext } from "./ContextVars";
import translations from "./translations";
import { phpPath } from "./config";

const PASS_KEY = "analyticsAdminPass";

// yyyy-mm-dd de hace N días
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const today = () => new Date().toISOString().slice(0, 10);

const num = (v) => Number(v || 0).toLocaleString();

// Bandera del país a partir del código ISO (GT -> 🇬🇹). Truco de
// los regional indicators, sin librería ni imágenes. TG
const flag = (code) => {
  if (!code || code.length !== 2) return "";
  try {
    return String.fromCodePoint(
      ...code.toUpperCase().split("").map((c) => 127397 + c.charCodeAt(0))
    );
  } catch (e) {
    return "";
  }
};

const Analytics = () => {
  const { language } = useContext(AppContext);
  const t = (key) => translations.Analytics[key]?.[language] || key;

  const [passphrase, setPassphrase] = useState(sessionStorage.getItem(PASS_KEY) || "");
  const [entered, setEntered] = useState(sessionStorage.getItem(PASS_KEY) || "");

  const [data, setData] = useState(null);
  const [owners, setOwners] = useState([]);
  const [currentHash, setCurrentHash] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  // filtros
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(today());
  const [site, setSite] = useState("all");
  const [includeOwner, setIncludeOwner] = useState(false);
  const [includeBots, setIncludeBots] = useState(false);

  //----------------------------
  // #Carga
  //----------------------------
  const load = useCallback(async (pass) => {
    if (!pass) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${phpPath}/analytics-summary.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ passphrase: pass, from, to, site, includeOwner, includeBots }),
      });
      const json = await res.json();
      if (json.success) {
        setData(json.data);
      } else {
        setError(json.message || t("loadError"));
        if ((json.message || "").indexOf("passphrase") !== -1) {
          sessionStorage.removeItem(PASS_KEY);
          setPassphrase("");
        }
      }
    } catch (e) {
      setError(t("networkError"));
    }
    setLoading(false);
  }, [from, to, site, includeOwner, includeBots, language]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadOwners = useCallback(async (pass) => {
    if (!pass) return;
    try {
      const res = await fetch(`${phpPath}/analytics-owners.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ passphrase: pass, action: "list" }),
      });
      const json = await res.json();
      if (json.success) {
        setOwners(json.data || []);
        setCurrentHash(json.currentHash || null);
      }
    } catch (e) {
      /* el dashboard sigue sirviendo sin esta parte */
    }
  }, []);

  useEffect(() => {
    if (passphrase) {
      load(passphrase);
      loadOwners(passphrase);
    }
  }, [passphrase, load, loadOwners]);

  //----------------------------
  // #Login
  //----------------------------
  const handleLogin = (e) => {
    e.preventDefault();
    sessionStorage.setItem(PASS_KEY, entered);
    setPassphrase(entered);
  };

  const handleLogout = () => {
    sessionStorage.removeItem(PASS_KEY);
    setPassphrase("");
    setEntered("");
    setData(null);
    setOwners([]);
  };

  //----------------------------
  // #Mis IPs
  //----------------------------
  const ownerAction = async (action, payload = {}) => {
    setBusy(action + (payload.ipHash || ""));
    try {
      const res = await fetch(`${phpPath}/analytics-owners.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ passphrase, action, ...payload }),
      });
      const json = await res.json();
      if (json.success) {
        await loadOwners(passphrase);
        await load(passphrase);
      } else {
        alert(json.message || t("loadError"));
      }
    } catch (e) {
      alert(t("networkError"));
    }
    setBusy("");
  };

  const setPreset = (days) => {
    setFrom(daysAgo(days - 1));
    setTo(today());
  };

  //----------------------------
  // #Login screen
  //----------------------------
  if (!passphrase) {
    return (
      <section className="section-2 overlay tint-black-40">
        <div className="section__content" style={{ position: "relative", zIndex: 1 }}>
          <h2 className="general-title">{t("title")}</h2>
          <form onSubmit={handleLogin} className="max-width-70" style={{ margin: "0 auto" }}>
            <p className="margin-top-20">{t("passphrase")}</p>
            <input
              type="password"
              value={entered}
              onChange={(e) => setEntered(e.target.value)}
              className="w-full border mb-2 p-2"
              style={{ color: "#000" }}
            />
            <button className="button-2 margin-top-10" type="submit">{t("enter")}</button>
          </form>
          {error && <p className="margin-top-10 font-size-18 color-gray-3">{error}</p>}
        </div>
      </section>
    );
  }

  const totals = data?.totals || {};
  const split = data?.split || {};
  const byDay = data?.byDay || [];
  const maxDay = Math.max(1, ...byDay.map((d) => Number(d.visitors || 0)));

  //----------------------------
  // #Tabla reusable
  //----------------------------
  // hideViews: para las tablas que solo traen visitantes (device,
  // browser, os) y si no saldría una columna llena de guiones. TG
  const StatTable = ({ title, rows, labelHead, showFlag, hideViews }) => (
    <div className="an-card">
      <h3 className="an-card__title">{title}</h3>
      {(!rows || rows.length === 0) ? (
        <p className="an-empty">{t("noData")}</p>
      ) : (
        <table className="an-table">
          <thead>
            <tr>
              <th>{labelHead}</th>
              <th className="an-num">{t("visitors")}</th>
              {!hideViews && <th className="an-num">{t("pageviews")}</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="an-label">
                  {showFlag && r.country_code ? flag(r.country_code) + " " : ""}
                  {r.label}
                </td>
                <td className="an-num">{num(r.visitors)}</td>
                {!hideViews && <td className="an-num">{num(r.pageviews)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <section className="section-2 noheight overlay tint-black-40">
      <div className="an-wrap" style={{ position: "relative", zIndex: 1 }}>

        <div className="an-header">
          <h2 className="general-h2">{t("title")}</h2>
          <button className="an-btn an-btn--ghost" onClick={handleLogout}>{t("logout")}</button>
        </div>

        {/* ---------------- filtros ---------------- */}
        <div className="an-filters">
          <div className="an-presets">
            <button className="an-btn" onClick={() => setPreset(7)}>{t("last7")}</button>
            <button className="an-btn" onClick={() => setPreset(30)}>{t("last30")}</button>
            <button className="an-btn" onClick={() => setPreset(90)}>{t("last90")}</button>
          </div>

          <label className="an-field">
            <span>{t("from")}</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>

          <label className="an-field">
            <span>{t("to")}</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>

          <label className="an-field">
            <span>{t("site")}</span>
            <select value={site} onChange={(e) => setSite(e.target.value)}>
              <option value="all">{t("allSites")}</option>
              {(data?.sites || []).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>

          <label className="an-check">
            <input type="checkbox" checked={includeOwner} onChange={(e) => setIncludeOwner(e.target.checked)} />
            <span>{t("includeMine")}</span>
          </label>

          <label className="an-check">
            <input type="checkbox" checked={includeBots} onChange={(e) => setIncludeBots(e.target.checked)} />
            <span>{t("includeBots")}</span>
          </label>

          <button className="an-btn an-btn--primary" onClick={() => load(passphrase)} disabled={loading}>
            {loading ? t("loading") : t("refresh")}
          </button>
        </div>

        {error && <p className="an-error">{error}</p>}

        {/* ---------------- KPIs ---------------- */}
        <div className="an-kpis">
          <div className="an-kpi">
            <div className="an-kpi__value">{num(totals.visitors)}</div>
            <div className="an-kpi__label">{t("visitors")}</div>
          </div>
          <div className="an-kpi">
            <div className="an-kpi__value">{num(totals.sessions)}</div>
            <div className="an-kpi__label">{t("sessions")}</div>
          </div>
          <div className="an-kpi">
            <div className="an-kpi__value">{num(totals.pageviews)}</div>
            <div className="an-kpi__label">{t("pageviews")}</div>
          </div>
          <div className="an-kpi an-kpi--muted">
            <div className="an-kpi__value">{num(split.owner_views)}</div>
            <div className="an-kpi__label">{t("myVisits")}</div>
          </div>
          <div className="an-kpi an-kpi--muted">
            <div className="an-kpi__value">{num(split.bot_views)}</div>
            <div className="an-kpi__label">{t("botVisits")}</div>
          </div>
        </div>

        <p className="an-note">{t("countingNote")}</p>

        {/* ---------------- gráfica por día ---------------- */}
        <div className="an-card">
          <h3 className="an-card__title">{t("visitorsPerDay")}</h3>
          {byDay.length === 0 ? (
            <p className="an-empty">{t("noData")}</p>
          ) : (
            <div className="an-chart">
              {byDay.map((d) => {
                const h = Math.round((Number(d.visitors || 0) / maxDay) * 100);
                return (
                  <div
                    className="an-chart__col"
                    key={d.day}
                    title={`${d.day}  ${d.visitors} ${t("visitors")} / ${d.pageviews} ${t("pageviews")}`}
                  >
                    <div className="an-chart__bar" style={{ height: `${Math.max(h, 2)}%` }} />
                  </div>
                );
              })}
            </div>
          )}
          {byDay.length > 0 && (
            <div className="an-chart__axis">
              <span>{byDay[0].day}</span>
              <span>{byDay[byDay.length - 1].day}</span>
            </div>
          )}
        </div>

        {/* ---------------- tablas ---------------- */}
        <div className="an-grid">
          <StatTable title={t("topCountries")} rows={data?.topCountries} labelHead={t("country")} showFlag />
          <StatTable title={t("topPages")} rows={data?.topPages} labelHead={t("page")} />
          <StatTable title={t("topReferrers")} rows={data?.topReferrers} labelHead={t("source")} />
          <StatTable title={t("byDevice")} rows={data?.byDevice} labelHead={t("device")} hideViews />
          <StatTable title={t("byBrowser")} rows={data?.byBrowser} labelHead={t("browser")} hideViews />
          <StatTable title={t("byOs")} rows={data?.byOs} labelHead={t("os")} hideViews />
          <StatTable title={t("bySite")} rows={data?.bySite} labelHead={t("site")} />
        </div>

        {/* ---------------- mis IPs ---------------- */}
        <div className="an-card">
          <h3 className="an-card__title">{t("myIps")}</h3>
          <p className="an-help">{t("myIpsHelp")}</p>

          <button
            className="an-btn an-btn--primary"
            onClick={() => ownerAction("claimCurrent")}
            disabled={busy === "claimCurrent" || (currentHash && owners.some((o) => o.ip_hash === currentHash))}
          >
            {currentHash && owners.some((o) => o.ip_hash === currentHash)
              ? t("thisIpAlreadyMine")
              : busy === "claimCurrent" ? t("saving") : t("markThisIp")}
          </button>

          {owners.length === 0 ? (
            <p className="an-empty">{t("noIps")}</p>
          ) : (
            <table className="an-table margin-top-20">
              <thead>
                <tr>
                  <th>{t("label")}</th>
                  <th className="an-num">{t("events")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {owners.map((o) => (
                  <tr key={o.id}>
                    <td className="an-label">
                      {o.label}
                      {o.ip_hash === currentHash && <span className="an-tag">{t("thisOne")}</span>}
                      <div className="an-hash">{String(o.ip_hash).slice(0, 12)}…</div>
                    </td>
                    <td className="an-num">{num(o.events)}</td>
                    <td className="an-num">
                      <button
                        className="an-btn an-btn--ghost"
                        onClick={() => ownerAction("remove", { ipHash: o.ip_hash })}
                        disabled={busy === "remove" + o.ip_hash}
                      >
                        {t("remove")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ---------------- visitas recientes ---------------- */}
        <div className="an-card margin-bottom-100">
          <h3 className="an-card__title">{t("recentVisits")}</h3>
          <p className="an-help">{t("recentHelp")}</p>
          {(!data?.recent || data.recent.length === 0) ? (
            <p className="an-empty">{t("noData")}</p>
          ) : (
            <div className="an-scroll">
              <table className="an-table">
                <thead>
                  <tr>
                    <th>{t("when")}</th>
                    <th>{t("page")}</th>
                    <th>{t("country")}</th>
                    <th>{t("device")}</th>
                    <th>{t("source")}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent.map((r, i) => {
                    const mine = Number(r.is_owner) === 1;
                    const bot = Number(r.is_bot) === 1;
                    return (
                      <tr key={i} className={mine ? "an-row--mine" : bot ? "an-row--bot" : ""}>
                        <td className="an-when">{r.created_at}</td>
                        <td className="an-label">
                          {r.path}
                          {mine && <span className="an-tag">{t("mine")}</span>}
                          {bot && <span className="an-tag an-tag--bot">bot</span>}
                        </td>
                        <td>{flag(r.country_code)} {r.country}</td>
                        <td>{r.device} · {r.browser}</td>
                        <td>{r.referrer_host}</td>
                        <td className="an-num">
                          {!mine && (
                            <button
                              className="an-btn an-btn--ghost"
                              onClick={() => ownerAction("add", { ipHash: r.ip_hash, label: t("markedFromList") })}
                              disabled={busy === "add" + r.ip_hash}
                            >
                              {t("thisIsMe")}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* #added crédito que pide la licencia de los datos de país 3/11/26 TG */}
        <p className="margin-top-20 font-size-18 color-gray-3">
          <a href="https://db-ip.com" target="_blank" rel="noreferrer">IP Geolocation by DB-IP</a>
        </p>
      </div>
    </section>
  );
};

export default Analytics;
